/* vitgem-os2.js — behaviours for the OS 2.0 sections, ported from the prototype's
   DCLogic Component (Vitgem Home v2.dc.html) to plain vanilla JS.

   What lives here (and the hooks each behaviour looks for):
   1. Scroll reveal        — every `.vg-os2[data-screen-label]` except Announcement/
                             Header/Hero/Footer fades+rises in once (IO, reduced-motion off).
   2. Bar fills            — `[data-vg-bar]` scaleX(0→1) staggered, for the evidence bars.
   3. Compact header       — `[data-screen-label="Header"]` gets data-compact="1" past 120px.
   4. Burger menu          — `[data-vg-menu-toggle]` toggles `[data-vg-menu]`, aria-expanded,
                             Escape and backdrop close.
   5. Sticky buy bar       — `#vg-sticky-buy` gets data-in="1" once the Hero leaves the
                             viewport (it has its own CTA above the fold).
   6. Review rail          — `#vg-rev-track` + `#vg-rev-prev`/`#vg-rev-next`; arrows dim and
                             disable at either end so no click is a dead click.
   7. Brand film           — `#vg-brand-film` native <video>: muted autoplay in view, pause
                             out of view, resume position via localStorage. No Plyr — the
                             prototype's player chrome was a recorded tap-target exception.
   8. PDP gallery          — `#vg-track` + `#vg-thumbs` + `#vg-lb`: hover-switching thumbs,
                             hover-zoom flyout, full-screen <dialog> viewer (see wireGallery).

   Cart (count badge, drawer open, quick add) is NOT here — vitgem-pop.js already bridges
   to <vitgem-cart-drawer>; OS2 sections reuse its [data-vg-cart-*] hooks.

   Everything is idempotent (data-vg-*-wired guards) and re-runs on
   shopify:section:load so the theme editor stays live. */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- 1. scroll reveal ------------------------------------------------ */
  var revealIo = null;
  function observeReveals() {
    if (reduceMotion) return;
    if (!revealIo) {
      revealIo = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          e.target.style.opacity = '1';
          e.target.style.transform = 'none';
          revealIo.unobserve(e.target);
        });
      }, { rootMargin: '0px 0px -12% 0px', threshold: 0.06 });
    }
    var skip = ['Announcement', 'Header', 'Hero', 'Footer'];
    document.querySelectorAll('.vg-os2[data-screen-label]').forEach(function (el, i) {
      if (skip.indexOf(el.dataset.screenLabel) !== -1 || el.dataset.vgRevealed) return;
      el.dataset.vgRevealed = '1';
      // already on screen (or above) — never hide content the user can see
      if (el.getBoundingClientRect().top < window.innerHeight * 0.92) return;
      var d = (i % 3) * 0.06;
      el.style.opacity = '0';
      el.style.transform = 'translateY(24px)';
      el.style.transition =
        'opacity .65s cubic-bezier(.16,.84,.44,1) ' + d + 's, ' +
        'transform .65s cubic-bezier(.16,.84,.44,1) ' + d + 's';
      revealIo.observe(el);
    });
  }
  function revealAll() {
    document.querySelectorAll('[data-vg-revealed]').forEach(function (el) {
      el.style.opacity = '1';
      el.style.transform = 'none';
      if (revealIo) revealIo.unobserve(el);
    });
  }
  // safety net: nothing may stay invisible if IO misfires, and print gets everything
  setTimeout(revealAll, 2000);
  window.addEventListener('beforeprint', revealAll);

  /* ---- 2. evidence bars ------------------------------------------------ */
  var barIo = null;
  function observeBars() {
    var bars = Array.prototype.filter.call(
      document.querySelectorAll('[data-vg-bar]'),
      function (b) { return !b.dataset.vgBarWired; }
    );
    if (!bars.length) return;
    if (!barIo) {
      barIo = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          var el = e.target;
          el.style.transition =
            'transform 1.15s cubic-bezier(.19,.9,.28,1) ' + (el.dataset.vgBarDelay || '0') + 's';
          el.style.transform = 'scaleX(1)';
          barIo.unobserve(el);
        });
      }, { rootMargin: '0px 0px -18% 0px', threshold: 0.2 });
    }
    bars.forEach(function (el, i) {
      el.dataset.vgBarWired = '1';
      if (reduceMotion) return;
      el.dataset.vgBarDelay = String(i * 0.14);
      el.style.transform = 'scaleX(0)';
      barIo.observe(el);
    });
  }

  /* ---- 3. compact header ----------------------------------------------- */
  var headScrollWired = false;
  function wireHeader() {
    var head = document.querySelector('[data-screen-label="Header"]');
    if (!head || headScrollWired) return;
    headScrollWired = true;
    var onScroll = function () {
      var y = window.scrollY || document.documentElement.scrollTop;
      head.setAttribute('data-compact', y > 120 ? '1' : '0');
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---- 4. burger menu --------------------------------------------------- */
  function wireMenu() {
    document.querySelectorAll('[data-vg-menu-toggle]').forEach(function (btn) {
      if (btn.dataset.vgWired) return;
      btn.dataset.vgWired = '1';
      btn.addEventListener('click', function () {
        var menu = document.querySelector('[data-vg-menu]');
        if (!menu) return;
        var open = menu.getAttribute('data-open') === '1';
        menu.setAttribute('data-open', open ? '0' : '1');
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        document.documentElement.style.overflow = open ? '' : 'hidden';
      });
    });
    document.querySelectorAll('[data-vg-menu-close]').forEach(function (el) {
      if (el.dataset.vgWired) return;
      el.dataset.vgWired = '1';
      el.addEventListener('click', closeMenu);
    });
    if (!document.documentElement.dataset.vgMenuEsc) {
      document.documentElement.dataset.vgMenuEsc = '1';
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') closeMenu();
      });
    }
  }
  function closeMenu() {
    var menu = document.querySelector('[data-vg-menu]');
    if (!menu || menu.getAttribute('data-open') !== '1') return;
    menu.setAttribute('data-open', '0');
    document.documentElement.style.overflow = '';
    var btn = document.querySelector('[data-vg-menu-toggle]');
    if (btn) { btn.setAttribute('aria-expanded', 'false'); btn.focus(); }
  }

  /* ---- 5. sticky buy bar ------------------------------------------------ */
  var heroIo = null;
  function wireStickyBuy() {
    var hero = document.querySelector('[data-screen-label="Hero"]');
    var bar = document.getElementById('vg-sticky-buy');
    if (!hero || !bar || heroIo) return;
    heroIo = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        bar.setAttribute('data-in', e.isIntersecting ? '0' : '1');
      });
    }, { threshold: 0, rootMargin: '-64px 0px 0px 0px' });
    heroIo.observe(hero);
  }

  /* ---- 5b. Pandectes cookie badge ----------------------------------------
     The consent app's "reopen" badge (button.widget.bottom.left inside the open
     shadow root of <pandectes-cmp>) sat on the sticky buy bar's text at 375px
     (measured 18 Oct 2026: badge 16–57 x 755–796 over "From 21p a day"). The app
     exposes no CSS part, so the only theme-side handle is a <style> appended
     inside its open shadow root. Phones only; desktop has no sticky bar. */
  function wirePandectesOffset() {
    var cmp = document.querySelector('pandectes-cmp');
    if (!cmp || !cmp.shadowRoot || cmp.shadowRoot.getElementById('vg-pandectes-offset')) return !!(cmp && cmp.shadowRoot);
    var st = document.createElement('style');
    st.id = 'vg-pandectes-offset';
    st.textContent = '@media (max-width:700px){.widget.bottom.left,.widget.bottom.right{bottom:96px!important}}';
    cmp.shadowRoot.appendChild(st);
    return true;
  }
  if (!wirePandectesOffset()) {
    var pandectesMo = new MutationObserver(function () {
      if (wirePandectesOffset()) pandectesMo.disconnect();
    });
    pandectesMo.observe(document.documentElement, { childList: true, subtree: true });
    setTimeout(function () { pandectesMo.disconnect(); }, 15000);
  }

  /* ---- 6. review rail --------------------------------------------------- */
  function wireReviewRail() {
    var rail = document.getElementById('vg-rev-track');
    var prev = document.getElementById('vg-rev-prev');
    var next = document.getElementById('vg-rev-next');
    if (!rail || !prev || !next || rail.dataset.vgWired) return;
    rail.dataset.vgWired = '1';
    var step = function () { return rail.firstElementChild ? rail.firstElementChild.offsetWidth + 22 : 320; };
    prev.addEventListener('click', function () { rail.scrollBy({ left: -step(), behavior: 'smooth' }); });
    next.addEventListener('click', function () { rail.scrollBy({ left: step(), behavior: 'smooth' }); });
    var setBtn = function (btn, off) {
      btn.disabled = off;
      btn.style.opacity = off ? '.34' : '1';
      btn.style.cursor = off ? 'default' : 'pointer';
    };
    var onRail = function () {
      var max = rail.scrollWidth - rail.clientWidth;
      setBtn(prev, rail.scrollLeft <= 2);
      setBtn(next, rail.scrollLeft >= max - 2);
    };
    rail.addEventListener('scroll', onRail, { passive: true });
    window.addEventListener('resize', onRail);
    onRail();
  }

  /* ---- 7. brand film ---------------------------------------------------- */
  function wireFilm() {
    var v = document.getElementById('vg-brand-film');
    if (!v || v.dataset.vgWired) return;
    v.dataset.vgWired = '1';
    v.muted = true;
    v.loop = true;
    v.controls = true;
    v.playsInline = true;
    v.setAttribute('playsinline', '');
    var KEY = 'vg-brand-film-t';
    var saved = parseFloat(localStorage.getItem(KEY) || '0');
    var seek = function () {
      if (saved > 0.5 && saved < v.duration - 1) v.currentTime = saved;
    };
    if (v.readyState >= 1) seek();
    else v.addEventListener('loadedmetadata', seek, { once: true });
    var lastSave = 0;
    v.addEventListener('timeupdate', function () {
      var now = Date.now();
      if (now - lastSave > 1000) {
        lastSave = now;
        try { localStorage.setItem(KEY, String(v.currentTime)); } catch (e) { /* private mode */ }
      }
    });
    // muted autoplay when scrolled into view, pause when it leaves
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) v.play().catch(function () {});
        else if (!v.paused) v.pause();
      });
    }, { threshold: 0.45 });
    io.observe(v);
  }

  /* ---- 8. subscribe frequency toggle ------------------------------------ */
  /* The two cadences are separate selling plans with separate allocations —
     monthly is the deeper discount — so a cadence swaps the price and the
     saving as well as the note. Each button carries its own allocation figures
     from Liquid; nothing here computes or hard-codes a number. */
  function wireSubToggle() {
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-vg-sub-toggle]'),
      function (group) {
        if (group.dataset.vgSubWired) return;
        group.dataset.vgSubWired = '1';
        var section = group.closest('section') || document;
        var note = section.querySelector('[data-vg-sub-note]');
        var priceEl = section.querySelector('[data-vg-sub-card-price]');
        var pctEl = section.querySelector('[data-vg-sub-card-pct]');
        var btns = group.querySelectorAll('[data-vg-sub-btn]');
        Array.prototype.forEach.call(btns, function (btn) {
          btn.addEventListener('click', function () {
            Array.prototype.forEach.call(btns, function (b) {
              b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
            });
            if (note && btn.dataset.note) note.textContent = btn.dataset.note;
            if (priceEl && btn.dataset.price) priceEl.textContent = btn.dataset.price;
            if (pctEl && btn.dataset.pct) pctEl.textContent = btn.dataset.pct;
          });
        });
      }
    );
  }

  /* ---- 9. FAQ open-state sync -------------------------------------------- */
  /* Sections use native <details>; the CSS styles the +/- medallion via the
     .open class the prototype's JS toggled — mirror it off the toggle event. */
  function wireFaq() {
    Array.prototype.forEach.call(
      document.querySelectorAll('.vg-os2 details.vg-faq-item'),
      function (d) {
        if (d.dataset.vgFaqWired) return;
        d.dataset.vgFaqWired = '1';
        d.classList.toggle('open', d.open);
        d.addEventListener('toggle', function () {
          d.classList.toggle('open', d.open);
        });
      }
    );
  }

  /* ---- 10. PDP: gallery, plan picker, quantity, sticky bar --------------- */
  /* The add itself is vitgem-pop.js's quick-add bridge (form[data-vg-quick-add]);
     this only keeps the UI state (selected plan, quantity, price totals) and the
     form's hidden inputs in sync. GBP-only store, so £ formatting is local. */
  function money(pence) {
    return '£' + (pence / 100).toFixed(2);
  }
  function wirePdp() {
    var box = document.getElementById('vg-buybox');
    if (!box || box.dataset.vgWired) return;
    box.dataset.vgWired = '1';
    var form = box.querySelector('form[data-vg-quick-add]');
    var qtyInput = box.querySelector('[data-vg-qty-input]');
    var planInput = box.querySelector('[data-vg-plan-input]');
    var qtyDisplay = box.querySelector('[data-vg-qty-display]');
    var planBtns = box.querySelectorAll('[data-vg-plan]');
    var stickyPlan = document.querySelector('[data-vg-sticky-plan]');
    var freqPanel = box.querySelector('[data-vg-freq-panel]');
    var freqBtns = box.querySelectorAll('[data-vg-freq]');
    var subBtn = box.querySelector('[data-vg-plan="sub"]');
    var subPriceEl = box.querySelector('[data-vg-sub-price]');
    var subSaveEl = box.querySelector('[data-vg-sub-save]');
    var freqTermsEl = box.querySelector('[data-vg-freq-terms]');
    var deliveryLine = box.querySelector('[data-vg-delivery-line]');
    var deliveryNote = box.querySelector('[data-vg-delivery-note]');
    var qty = 1;
    var unit = 0;
    planBtns.forEach(function (b) {
      if (b.getAttribute('aria-pressed') === 'true') unit = parseInt(b.dataset.price, 10) || 0;
    });

    function renderTotals() {
      var total = money(unit * qty);
      document.querySelectorAll('[data-vg-total]').forEach(function (el) { el.textContent = total; });
      var st = document.querySelector('[data-vg-sticky-total]');
      if (st) st.textContent = total;
    }

    // the sticky bar names the cadence rather than a bare "subscription", so a
    // shopper scrolled past the buybox still reads which plan is selected
    function subLabel() {
      var on = box.querySelector('[data-vg-freq][aria-pressed="true"]');
      var span = on && on.querySelector('[data-vg-freq-name]');
      var name = span ? span.textContent.trim().toLowerCase() : '';
      if (!name) return 'subscription';
      return name === 'monthly' ? 'monthly subscription' : name;
    }

    planBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        planBtns.forEach(function (b) {
          var on = b === btn;
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
          var ring = b.querySelector('[data-vg-plan-ring]');
          var dot = b.querySelector('[data-vg-plan-dot]');
          if (ring) ring.style.display = on ? '' : 'none';
          if (dot) dot.style.display = on ? '' : 'none';
        });
        var sub = btn.dataset.vgPlan === 'sub';
        if (planInput) planInput.disabled = !sub;
        if (stickyPlan) stickyPlan.textContent = sub ? subLabel() : 'one-time';
        if (freqPanel) freqPanel.style.display = sub ? '' : 'none';
        // every subscription ships free, so the delivery line states that while the
        // subscribe plan is picked and reverts to the dispatch note otherwise
        if (deliveryLine && deliveryNote && deliveryLine.dataset.sub) {
          deliveryNote.textContent = sub ? deliveryLine.dataset.sub : deliveryLine.dataset.onetime;
        }
        unit = parseInt(btn.dataset.price, 10) || 0;
        renderTotals();
      });
    });

    // delivery frequency: each cadence is its own selling plan with its own
    // allocation, so picking one re-prices the subscribe option itself
    freqBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        // aria-pressed is the only state written here — vitgem-os2.css paints the
        // selected cadence off that attribute (white card, ink offset shadow, filled dot)
        freqBtns.forEach(function (b) {
          b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
        });
        var price = parseInt(btn.dataset.price, 10) || 0;
        if (planInput) planInput.value = btn.dataset.vgFreq;
        if (subBtn) subBtn.dataset.price = String(price);
        if (subPriceEl) subPriceEl.textContent = money(price);
        if (subSaveEl) subSaveEl.textContent = 'Save ' + btn.dataset.save + ' every delivery';
        if (freqTermsEl) freqTermsEl.textContent = btn.dataset.terms || '';
        if (subBtn && subBtn.getAttribute('aria-pressed') === 'true') {
          unit = price;
          if (stickyPlan) stickyPlan.textContent = subLabel();
          renderTotals();
        }
      });
    });

    box.querySelectorAll('[data-vg-qty]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        qty = Math.min(10, Math.max(1, qty + parseInt(btn.dataset.vgQty, 10)));
        if (qtyDisplay) qtyDisplay.textContent = String(qty);
        if (qtyInput) qtyInput.value = String(qty);
        renderTotals();
      });
    });

    // sticky bar: in once the buybox has scrolled away, out when it returns
    var sticky = document.getElementById('vg-pdp-sticky');
    if (sticky) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          sticky.setAttribute('data-in', e.isIntersecting ? '0' : '1');
        });
      }, { threshold: 0, rootMargin: '-64px 0px 0px 0px' });
      io.observe(box);
      var stickyAdd = sticky.querySelector('[data-vg-sticky-add]');
      if (stickyAdd && form) {
        stickyAdd.addEventListener('click', function () {
          if (typeof form.requestSubmit === 'function') form.requestSubmit();
          else form.submit();
        });
      }
    }
  }

  /* ---- 10b. PDP gallery — the amazon.co.uk browse model ------------------ */
  /* The track is a CSS scroll-snap container and the page thumbnails are anchors
     to the slide ids, so switching photographs already works with this file
     absent. Everything below is the upgrade: thumbnails that switch on hover, a
     2.5x flyout to the right of the frame with a lens on the frame, the
     full-screen <dialog> viewer (click, tap, Enter or the expand button) with
     zoom, pan, pinch and swipe, and an observer that keeps the selected
     thumbnail in step with a phone swipe — which no click handler can see.
     Hooks: #vg-gallery #vg-frame #vg-track [data-vg-slide][data-vg-zoom]
     #vg-thumbs [data-vg-thumb] [data-vg-gal-count] [data-vg-open] [data-vg-hint]
     [data-vg-lens] [data-vg-pane] #vg-lb [data-vg-lb-*]. */
  function wireGallery() {
    var track = document.getElementById('vg-track');
    // init() re-runs on shopify:section:load in the theme editor; without this
    // the listeners would stack up on every save.
    if (!track || track.dataset.vgGalBound) return;
    track.dataset.vgGalBound = '1';
    var slides = Array.prototype.slice.call(track.querySelectorAll('[data-vg-slide]'));
    var total = slides.length;
    if (!total) return;
    var frame = document.getElementById('vg-frame') || track.parentNode;
    var gallery = document.getElementById('vg-gallery') || frame.parentNode;
    var thumbStrip = document.getElementById('vg-thumbs');
    var thumbs = thumbStrip ? Array.prototype.slice.call(thumbStrip.querySelectorAll('[data-vg-thumb]')) : [];
    var pill = gallery.querySelector('[data-vg-gal-count]');
    var openBtn = gallery.querySelector('[data-vg-open]');
    var hint = gallery.querySelector('[data-vg-hint]');
    var lens = gallery.querySelector('[data-vg-lens]');
    var pane = gallery.querySelector('[data-vg-pane]');
    var paneImg = pane ? pane.querySelector('img') : null;
    var dialog = document.getElementById('vg-lb');
    var canModal = !!(dialog && typeof dialog.showModal === 'function');
    // A theme-editor re-render while the viewer was open leaves the page locked.
    if (document.documentElement.style.overflow === 'hidden' && !document.querySelector('dialog[open]')) {
      document.documentElement.style.overflow = '';
    }

    var SCALE = 2.5;
    var MAX_SCALE = 4;
    var hoverMq = window.matchMedia('(hover:hover) and (pointer:fine) and (min-width:1001px)');
    var current = 0;
    var zoomCache = {};
    var clamp = function (v, lo, hi) { return Math.min(hi, Math.max(lo, v)); };
    var wrap = function (i) { return ((i % total) + total) % total; };
    var slideImg = function (i) { return slides[i].querySelector('img'); };
    var slideAlt = function (i) { var im = slideImg(i); return im ? im.alt : ''; };
    // The file the page has already painted for a slide — a cache hit — or ''
    // when a lazy slide has not loaded yet.
    var paintedSrc = function (i) {
      var im = slideImg(i);
      return (im && im.complete && im.currentSrc) ? im.currentSrc : '';
    };
    var zoomSrc = function (i) { return slides[i].getAttribute('data-vg-zoom') || paintedSrc(i); };
    var zoomReady = function (i) { var c = zoomCache[i]; return !!(c && c.complete && c.naturalWidth); };
    // The 2048px file is requested here and nowhere else: on a hover, on opening
    // the viewer, on switching inside it. Never on page load.
    var loadZoom = function (i, cb) {
      if (zoomReady(i)) { cb(zoomSrc(i)); return; }
      var c = zoomCache[i];
      if (!c) {
        c = new Image();
        c.src = zoomSrc(i);
        zoomCache[i] = c;
      }
      c.addEventListener('load', function () { cb(zoomSrc(i)); }, { once: true });
    };

    /* -- selection state: one source of truth for the page and the viewer -- */
    var lbThumbs = dialog ? Array.prototype.slice.call(dialog.querySelectorAll('[data-vg-lb-thumb]')) : [];
    var lbStrip = dialog ? dialog.querySelector('[data-vg-lb-thumbs]') : null;
    var lbCount = dialog ? dialog.querySelector('[data-vg-lb-count]') : null;
    var setCurrent = function (list, i) {
      list.forEach(function (t, j) {
        if (j === i) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
    };
    // Keep the selected tile inside its own strip by moving the strip's own
    // scroll offset — never scrollIntoView, which also drags the page.
    var centreIn = function (strip, tile) {
      if (!strip || !tile) return;
      var opts = { behavior: reduceMotion ? 'auto' : 'smooth' };
      if (strip.scrollWidth > strip.clientWidth + 1) {
        opts.left = tile.offsetLeft - (strip.clientWidth - tile.offsetWidth) / 2;
      }
      if (strip.scrollHeight > strip.clientHeight + 1) {
        opts.top = tile.offsetTop - (strip.clientHeight - tile.offsetHeight) / 2;
      }
      if (opts.left !== undefined || opts.top !== undefined) strip.scrollTo(opts);
    };
    var mark = function (i) {
      if (i === current) return;
      current = i;
      setCurrent(thumbs, i);
      setCurrent(lbThumbs, i);
      if (pill) pill.textContent = (i + 1) + ' / ' + total;
      if (lbCount) lbCount.textContent = (i + 1) + ' / ' + total;
      centreIn(thumbStrip, thumbs[i]);
      centreIn(lbStrip, lbThumbs[i]);
      if (frame.dataset.vgZooming === '1') startZoom();
    };
    // While a programmatic scroll is in flight the observer would mark every
    // slide it passes; hold it until the target lands (or 800ms, whichever first).
    var pending = -1;
    var pendingTimer = 0;
    var goTo = function (i) {
      i = clamp(i, 0, total - 1);
      mark(i);
      pending = i;
      clearTimeout(pendingTimer);
      pendingTimer = setTimeout(function () { pending = -1; }, 800);
      // behavior:'auto' defers to the CSS scroll-behavior: smooth on a phone,
      // instant on a desktop and under reduced motion.
      track.scrollTo({ left: i * track.clientWidth, top: 0, behavior: 'auto' });
    };
    if ('IntersectionObserver' in window) {
      var gio = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          var idx = slides.indexOf(e.target);
          if (pending >= 0) {
            if (idx !== pending) return;
            pending = -1;
          }
          mark(idx);
        });
      }, { root: track, threshold: 0.5 });
      slides.forEach(function (sl) { gio.observe(sl); });
    }

    thumbs.forEach(function (t, j) {
      t.addEventListener('click', function (e) { e.preventDefault(); goTo(j); });
      t.addEventListener('pointerenter', function (e) {
        if (e.pointerType === 'mouse' && hoverMq.matches) goTo(j);
      });
      t.addEventListener('focus', function () { goTo(j); });
    });

    track.addEventListener('keydown', function (e) {
      var k = e.key;
      if (k === 'ArrowRight') { e.preventDefault(); goTo(current + 1); }
      else if (k === 'ArrowLeft') { e.preventDefault(); goTo(current - 1); }
      else if (k === 'Home') { e.preventDefault(); goTo(0); }
      else if (k === 'End') { e.preventDefault(); goTo(total - 1); }
      else if ((k === 'Enter' || k === ' ') && canModal) { e.preventDefault(); openLb(current); }
    });
    if (canModal) {
      // A touch scroll never emits click, so on a phone only a real tap opens it.
      track.addEventListener('click', function () { openLb(current); });
      if (openBtn) {
        openBtn.hidden = false;
        openBtn.addEventListener('click', function () { openLb(current); });
      }
    }
    if (pane && paneImg && lens && hint) hint.hidden = false;

    /* -- hover zoom: lens on the frame, 2.5x flyout to its right ------------ */
    var geo = null;
    var lastMove = null;
    var zoomRaf = 0;
    var zoomTry = 0;
    var placeZoom = function (pt) {
      if (!geo || !pt) return;
      var rect = track.getBoundingClientRect();
      var mx = pt.clientX - rect.left - track.clientLeft;
      var my = pt.clientY - rect.top - track.clientTop;
      var lx = clamp(mx - geo.lw / 2, 0, geo.W - geo.lw);
      var ly = clamp(my - geo.lh / 2, 0, geo.H - geo.lh);
      lens.style.transform = 'translate(' + (lx + track.clientLeft) + 'px,' + (ly + track.clientTop) + 'px)';
      // Panel pixel p shows frame coordinate lx + p/SCALE, and the drawn image
      // starts at dx — so its origin sits at (dx - lx) * SCALE.
      paneImg.style.transform = 'translate3d(' + ((geo.dx - lx) * SCALE) + 'px,' + ((geo.dy - ly) * SCALE) + 'px,0)';
    };
    var startZoom = function () {
      if (!pane || !paneImg || !lens) return;
      var im = slideImg(current);
      if (!im || !im.naturalWidth) return; // nothing painted yet — nothing to magnify
      var W = track.clientWidth;
      var H = track.clientHeight;
      // Read the gutter off the slide: the render slide has a wider one.
      var P = parseFloat(window.getComputedStyle(im).paddingLeft) || 0;
      var bw = W - 2 * P;
      var bh = H - 2 * P;
      var r = Math.min(bw / im.naturalWidth, bh / im.naturalHeight);
      var dw = im.naturalWidth * r;
      var dh = im.naturalHeight * r;
      var dx = P + (bw - dw) / 2;
      var dy = P + (bh - dh) / 2;
      pane.style.width = '';
      frame.dataset.vgZooming = '1';
      // Between ~1170 and 1250px the panel would run off the right edge: shrink
      // it by the overflow (the lens goes oblong to match); under 280px give up.
      var pr = pane.getBoundingClientRect();
      var over = pr.right + 16 - document.documentElement.clientWidth;
      if (over > 0) pane.style.width = Math.floor(pr.width - over) + 'px';
      if (pane.clientWidth < 280) { frame.dataset.vgZooming = '0'; geo = null; return; }
      var lw = pane.clientWidth / SCALE;
      var lh = pane.clientHeight / SCALE;
      lens.style.width = lw + 'px';
      lens.style.height = lh + 'px';
      paneImg.style.width = (dw * SCALE) + 'px';
      paneImg.style.height = (dh * SCALE) + 'px';
      var idx = current;
      if (zoomReady(idx)) {
        paneImg.src = zoomSrc(idx);
      } else {
        var base = paintedSrc(idx);
        if (base) paneImg.src = base;
        loadZoom(idx, function (url) {
          if (frame.dataset.vgZooming === '1' && current === idx) paneImg.src = url;
        });
      }
      geo = { W: W, H: H, dx: dx, dy: dy, lw: lw, lh: lh };
      placeZoom(lastMove);
    };
    if (pane && paneImg && lens) {
      var isMouse = function (e) { return e.pointerType === 'mouse' && hoverMq.matches; };
      frame.addEventListener('pointerenter', function (e) {
        if (!isMouse(e)) return;
        lastMove = { clientX: e.clientX, clientY: e.clientY };
        zoomTry = Date.now();
        startZoom();
      });
      frame.addEventListener('pointermove', function (e) {
        if (!isMouse(e)) return;
        lastMove = { clientX: e.clientX, clientY: e.clientY };
        if (frame.dataset.vgZooming !== '1') {
          // The slide may not have been painted on enter, or the viewer just
          // closed under the cursor: retry, but no more than every 300ms.
          if (Date.now() - zoomTry > 300) { zoomTry = Date.now(); startZoom(); }
          return;
        }
        if (!zoomRaf) {
          zoomRaf = window.requestAnimationFrame(function () { zoomRaf = 0; placeZoom(lastMove); });
        }
      });
      frame.addEventListener('pointerleave', function (e) {
        if (e.pointerType !== 'mouse') return;
        frame.dataset.vgZooming = '0';
        lastMove = null;
      });
      // A resize moves the right edge the panel was clamped against: drop the
      // zoom and let the next mouse move re-measure. Self-removes once the
      // theme editor has replaced the section.
      var onFrameResize = function () {
        if (!document.contains(frame)) { window.removeEventListener('resize', onFrameResize); return; }
        frame.dataset.vgZooming = '0';
        geo = null;
      };
      window.addEventListener('resize', onFrameResize);
    }

    /* -- full-screen viewer --------------------------------------------- */
    if (!canModal) return;
    var lbView = dialog.querySelector('[data-vg-lb-view]');
    var lbImg = dialog.querySelector('[data-vg-lb-img]');
    var lbStage = dialog.querySelector('[data-vg-lb-stage]');
    var lbPrev = dialog.querySelector('[data-vg-lb-prev]');
    var lbNext = dialog.querySelector('[data-vg-lb-next]');
    var lbClose = dialog.querySelector('[data-vg-lb-close]');
    var lbZoom = dialog.querySelector('[data-vg-lb-zoom]');
    if (!lbView || !lbImg) return;
    var returnTo = null;
    var s = 1;
    var tx = 0;
    var ty = 0;
    var panRaf = 0;

    // The <img> is the view's own size and scales about its top-left corner, so
    // a translate of [W - W*s, 0] keeps the photograph covering the window.
    var setTransform = function () {
      var W = lbView.clientWidth;
      var H = lbView.clientHeight;
      tx = clamp(tx, W - W * s, 0);
      ty = clamp(ty, H - H * s, 0);
      lbImg.style.transform = 'translate3d(' + tx + 'px,' + ty + 'px,0) scale(' + s + ')';
      var z = s > 1.001;
      lbView.dataset.zoomed = z ? '1' : '0';
      if (lbZoom) {
        lbZoom.setAttribute('aria-pressed', z ? 'true' : 'false');
        lbZoom.setAttribute('aria-label', z ? 'Zoom out' : 'Zoom in');
      }
    };
    var schedule = function () {
      if (!panRaf) panRaf = window.requestAnimationFrame(function () { panRaf = 0; setTransform(); });
    };
    // Scale about (ox, oy) in view coordinates: the point under the cursor or
    // between two fingers stays put.
    var zoomTo = function (s2, ox, oy) {
      s2 = clamp(s2, 1, MAX_SCALE);
      tx = ox - (ox - tx) * (s2 / s);
      ty = oy - (oy - ty) * (s2 / s);
      s = s2;
      if (s === 1) { tx = 0; ty = 0; }
      setTransform();
    };
    var resetZoom = function () { s = 1; tx = 0; ty = 0; setTransform(); };
    var toggleZoom = function (x, y) {
      if (s > 1) resetZoom();
      else zoomTo(SCALE, x, y);
    };

    var show = function (i) {
      i = wrap(i);
      mark(i);
      lbImg.alt = slideAlt(i);
      if (zoomReady(i)) {
        lbImg.src = zoomSrc(i);
      } else {
        var base = paintedSrc(i);
        lbImg.src = base || zoomSrc(i);
        if (base) loadZoom(i, function (url) { if (dialog.open && current === i) lbImg.src = url; });
      }
      resetZoom();
      // On a desktop, warm the neighbours so the arrows land on the sharp file.
      if (hoverMq.matches) {
        loadZoom(wrap(i + 1), function () {});
        loadZoom(wrap(i - 1), function () {});
      }
    };
    var onResize = function () {
      if (!document.contains(dialog)) { window.removeEventListener('resize', onResize); return; }
      setTransform();
    };
    var openLb = function (i) {
      if (dialog.open) return;
      returnTo = document.activeElement;
      frame.dataset.vgZooming = '0';
      document.documentElement.style.overflow = 'hidden';
      dialog.showModal();
      show(i);
      if (lbClose) lbClose.focus();
      window.addEventListener('resize', onResize);
    };
    // 'close' fires for Escape, the × button and dialog.close() alike.
    dialog.addEventListener('close', function () {
      document.documentElement.style.overflow = '';
      resetZoom();
      window.removeEventListener('resize', onResize);
      pending = -1;
      goTo(current);
      var rt = returnTo;
      returnTo = null;
      if (rt && document.contains(rt) && typeof rt.focus === 'function') rt.focus({ preventScroll: true });
      else track.focus({ preventScroll: true });
    });

    if (lbPrev) lbPrev.addEventListener('click', function () { show(current - 1); });
    if (lbNext) lbNext.addEventListener('click', function () { show(current + 1); });
    if (lbClose) lbClose.addEventListener('click', function () { dialog.close(); });
    if (lbZoom) lbZoom.addEventListener('click', function () { toggleZoom(lbView.clientWidth / 2, lbView.clientHeight / 2); });
    lbThumbs.forEach(function (t, j) {
      t.addEventListener('click', function () { show(j); });
      t.addEventListener('pointerenter', function (e) {
        if (e.pointerType === 'mouse' && hoverMq.matches) show(j);
      });
      t.addEventListener('focus', function () { show(j); });
    });
    if (lbStage) {
      // The cream around the photograph closes the viewer, like the backdrop would.
      lbStage.addEventListener('click', function (e) { if (e.target === lbStage) dialog.close(); });
    }
    dialog.addEventListener('keydown', function (e) {
      var k = e.key;
      if (k === 'ArrowRight') { e.preventDefault(); show(current + 1); }
      else if (k === 'ArrowLeft') { e.preventDefault(); show(current - 1); }
      else if (k === 'Home') { e.preventDefault(); show(0); }
      else if (k === 'End') { e.preventDefault(); show(total - 1); }
      else if (k === '+' || k === '=') { e.preventDefault(); zoomTo(SCALE, lbView.clientWidth / 2, lbView.clientHeight / 2); }
      else if (k === '-' || k === '_' || k === '0') { e.preventDefault(); resetZoom(); }
    });

    // Pointer model on the view. Mouse: click toggles 2.5x about the cursor and,
    // once zoomed, the cursor's position pans (the amazon.co.uk behaviour — no
    // dragging needed). Touch: double-tap toggles, pinch scales 1–4x, a drag
    // pans while zoomed, and a horizontal swipe at 1x changes the photograph.
    var pts = {};
    var nPts = 0;
    var down = null;
    var moved = false;
    var pinch = null;
    var lastTap = null;
    var swipeDx = 0;
    var ptsArr = function () { return Object.keys(pts).map(function (k) { return pts[k]; }); };
    var dist = function (a, b) { return Math.hypot(a.x - b.x, a.y - b.y); };
    var mid = function (a, b) { return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }; };
    var local = function (e) {
      var r = lbView.getBoundingClientRect();
      return { x: e.clientX - r.left - lbView.clientLeft, y: e.clientY - r.top - lbView.clientTop };
    };
    lbView.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      e.preventDefault();
      try { lbView.setPointerCapture(e.pointerId); } catch (err) { /* stale pointer id */ }
      var p = local(e);
      pts[e.pointerId] = p;
      nPts++;
      if (nPts === 2) {
        var a = ptsArr();
        pinch = { d: dist(a[0], a[1]) || 1, s: s, m: mid(a[0], a[1]), tx: tx, ty: ty };
        down = null;
        moved = true;
      } else if (nPts === 1) {
        down = { x: p.x, y: p.y, type: e.pointerType };
        moved = false;
        swipeDx = 0;
        pinch = null;
      }
    });
    lbView.addEventListener('pointermove', function (e) {
      var p = local(e);
      if (pts[e.pointerId]) {
        var prev = pts[e.pointerId];
        pts[e.pointerId] = p;
        if (pinch && nPts >= 2) {
          var a = ptsArr();
          var m = mid(a[0], a[1]);
          var s2 = clamp(pinch.s * dist(a[0], a[1]) / pinch.d, 1, MAX_SCALE);
          // scale about the first midpoint, then follow the midpoint's travel
          tx = pinch.m.x - (pinch.m.x - pinch.tx) * (s2 / pinch.s) + (m.x - pinch.m.x);
          ty = pinch.m.y - (pinch.m.y - pinch.ty) * (s2 / pinch.s) + (m.y - pinch.m.y);
          s = s2;
          schedule();
        } else if (down && nPts === 1) {
          if (!moved && Math.hypot(p.x - down.x, p.y - down.y) > 8) moved = true;
          if (s > 1) {
            tx += p.x - prev.x;
            ty += p.y - prev.y;
            schedule();
          } else if (down.type !== 'mouse') {
            swipeDx = p.x - down.x;
          }
        }
        return;
      }
      if (e.pointerType === 'mouse' && !nPts && s > 1) {
        var W = lbView.clientWidth;
        var H = lbView.clientHeight;
        tx = -(clamp(p.x, 0, W) / W) * (W * s - W);
        ty = -(clamp(p.y, 0, H) / H) * (H * s - H);
        schedule();
      }
    });
    var endPointer = function (e) {
      if (!pts[e.pointerId]) return;
      delete pts[e.pointerId];
      nPts--;
      if (pinch) {
        if (nPts < 2) {
          pinch = null;
          down = null;
          if (s < 1.05) resetZoom(); else setTransform();
        }
        return;
      }
      if (!down) return;
      var p = local(e);
      var was = down;
      down = null;
      if (!moved) {
        if (was.type === 'mouse') {
          toggleZoom(p.x, p.y);
        } else {
          var now = Date.now();
          if (lastTap && now - lastTap.t < 300 && Math.hypot(p.x - lastTap.x, p.y - lastTap.y) < 30) {
            lastTap = null;
            toggleZoom(p.x, p.y);
          } else {
            lastTap = { t: now, x: p.x, y: p.y };
          }
        }
      } else if (s === 1 && was.type !== 'mouse' && Math.abs(swipeDx) >= 48 && Math.abs(swipeDx) > Math.abs(p.y - was.y)) {
        show(swipeDx < 0 ? current + 1 : current - 1);
      }
    };
    lbView.addEventListener('pointerup', endPointer);
    lbView.addEventListener('pointercancel', endPointer);
  }

  /* ---- 11. Collection: goal filter pills + sort -------------------------- */
  /* Pure display state: pills show/hide card wrappers by data-vg-formula, the
     sort select rewrites each wrapper's flex `order` (featured = authored
     order, price ascending, rating descending) and the count line follows. */
  function wireCollection() {
    var root = document.getElementById('vg-collection');
    if (!root || root.dataset.vgWired) return;
    root.dataset.vgWired = '1';
    var pills = root.querySelectorAll('[data-vg-filter]');
    var items = Array.prototype.slice.call(root.querySelectorAll('[data-vg-item]'));
    var count = root.querySelector('[data-vg-count]');
    var sort = root.querySelector('[data-vg-sort]');
    var active = 'all';

    function apply() {
      var visible = 0;
      items.forEach(function (el) {
        var show = active === 'all' || el.dataset.vgFormula === active;
        el.style.display = show ? '' : 'none';
        if (show) visible++;
      });
      if (count) count.textContent = visible + (visible === 1 ? ' formula' : ' formulas');
    }
    pills.forEach(function (pill) {
      pill.addEventListener('click', function () {
        active = pill.dataset.vgFilter;
        pills.forEach(function (p) {
          var on = p === pill;
          p.setAttribute('aria-pressed', on ? 'true' : 'false');
          var ring = p.querySelector('[data-vg-ring]');
          if (ring) ring.style.display = on ? '' : 'none';
        });
        apply();
      });
    });
    if (sort) {
      sort.addEventListener('change', function () {
        var mode = sort.value;
        var sorted = items.slice().sort(function (a, b) {
          if (mode === 'price') return (+a.dataset.vgPrice) - (+b.dataset.vgPrice);
          if (mode === 'rating') return (+b.dataset.vgRating) - (+a.dataset.vgRating);
          return (+a.dataset.vgOrder) - (+b.dataset.vgOrder);
        });
        sorted.forEach(function (el, i) { el.style.order = i; });
      });
    }
  }

  /* ---- 12. Bundles: build-your-own picker -------------------------------- */
  /* Each jar card carries a quantity, not a yes/no: the pick button toggles it
     on at one and the -/+ stepper takes it further. The store's multi-buy offer
     is a quantity ladder with no formula condition — two jars take
     data-vg-tier2, three or more take data-vg-tier3 — so nothing here counts
     distinct formulas, and two of the same jar earn exactly what two different
     ones do. Both percentages are display only; checkout applies the real
     automatic discount. Add posts every picked variant with its own quantity in
     one cart/add.js call and opens <vitgem-cart-drawer>, same path as
     vitgem-pop.js quick add. */
  function wireBuilder() {
    var bar = document.getElementById('vg-builder-bar');
    if (!bar || bar.dataset.vgWired) return;
    bar.dataset.vgWired = '1';
    var section = bar.closest('section') || document;
    var cards = Array.prototype.slice.call(section.querySelectorAll('[data-vg-jar]'));
    var pickLine = bar.querySelector('[data-vg-pick-line]');
    var totalEl = bar.querySelector('[data-vg-build-total]');
    var grossEl = bar.querySelector('[data-vg-build-gross]');
    var saveEl = bar.querySelector('[data-vg-build-save]');
    var addBtn = bar.querySelector('[data-vg-build-add]');
    var ctaEl = bar.querySelector('[data-vg-build-cta]');
    var ctaLabel = ctaEl ? ctaEl.textContent : 'Add to basket';
    var ladder = section.querySelector('[data-vg-ladder]');
    var ladderLine = ladder ? ladder.querySelector('[data-vg-ladder-line]') : null;
    var ladderFill = ladder ? ladder.querySelector('[data-vg-ladder-fill]') : null;
    var ladderTicks = ladder ? Array.prototype.slice.call(ladder.querySelectorAll('[data-vg-tick]')) : [];
    var tier2 = +bar.dataset.vgTier2 || 0;
    var tier3 = +bar.dataset.vgTier3 || 0;
    var maxQty = +bar.dataset.vgMax || 5;

    /* Quantity lives in the DOM (data-vg-qty) rather than a parallel array, so
       the markup and the maths cannot drift apart. */
    function qtyOf(card) { return +card.dataset.vgQty || 0; }
    function units() {
      var n = 0;
      cards.forEach(function (card) { n += qtyOf(card); });
      return n;
    }
    function grossOf() {
      var p = 0;
      cards.forEach(function (card) { p += (+card.dataset.vgPrice) * qtyOf(card); });
      return p;
    }
    function pctFor(n) { return n >= 3 ? tier3 : n >= 2 ? tier2 : 0; }

    function setQty(card, n) {
      if (n < 0) n = 0;
      if (n > maxQty) n = maxQty;
      card.dataset.vgQty = n;
      card.classList.toggle('is-picked', n > 0);
      var pick = card.querySelector('[data-vg-pick]');
      if (pick) pick.setAttribute('aria-pressed', n > 0 ? 'true' : 'false');
      var value = card.querySelector('[data-vg-qty-value]');
      if (value) value.textContent = n;
      var plus = card.querySelector('[data-vg-plus]');
      if (plus) plus.disabled = n >= maxQty;
    }

    function render() {
      var n = units();
      var gross = grossOf();
      var saving = Math.round(gross * pctFor(n) / 100);
      if (totalEl) totalEl.textContent = money(gross - saving);
      if (grossEl) {
        grossEl.textContent = money(gross);
        grossEl.style.display = saving > 0 ? '' : 'none';
      }
      if (saveEl) {
        saveEl.textContent = 'Save ' + money(saving);
        saveEl.style.display = saving > 0 ? '' : 'none';
      }
      /* The bar fills to the third tier and stops, however many jars are picked
         past it. The stops are the medallion centres, not thirds: the middle
         medallion sits at 50% of the rail and the last one at its right edge, so
         a filled bar always ends under the medallion it just earned. One jar
         lands short of the 10% medallion — progress, not a tier. */
      var step = Math.min(n, 3);
      if (ladderFill) ladderFill.style.width = ['0%', '25%', '50%', '100%'][step];
      ladderTicks.forEach(function (t) {
        t.classList.toggle('is-on', step >= (+t.dataset.vgTick || 0));
      });
      if (ladderLine && ladder) {
        /* getAttribute, not dataset: a hyphen before a digit is not camel-cased,
           so data-vg-line-0 lands in dataset under the key "vgLine-0". */
        var copy = ladder.getAttribute('data-vg-line-' + step);
        if (copy) ladderLine.textContent = copy;
      }
      if (pickLine) {
        pickLine.textContent = n === 0
          ? bar.dataset.vgEmptyLine
          : n === 1 ? '1 jar picked' : n + ' jars picked';
      }
      if (ctaEl) ctaEl.textContent = n < 2 ? ctaLabel : 'Add ' + n + ' jars';
      if (addBtn) {
        addBtn.disabled = n === 0;
        addBtn.style.opacity = n === 0 ? '.45' : '';
        addBtn.style.cursor = n === 0 ? 'not-allowed' : '';
        addBtn.style.boxShadow = n === 0 ? 'none' : '';
        addBtn.style.transform = n === 0 ? 'none' : '';
      }
    }

    cards.forEach(function (card) {
      var pick = card.querySelector('[data-vg-pick]');
      var minus = card.querySelector('[data-vg-minus]');
      var plus = card.querySelector('[data-vg-plus]');
      if (pick) {
        pick.addEventListener('click', function () {
          setQty(card, qtyOf(card) > 0 ? 0 : 1);
          render();
        });
      }
      /* Down from one deselects the jar outright — there is no "picked, zero of
         it" state to leave the shopper in. */
      if (minus) {
        minus.addEventListener('click', function () {
          setQty(card, qtyOf(card) - 1);
          render();
        });
      }
      if (plus) {
        plus.addEventListener('click', function () {
          setQty(card, qtyOf(card) + 1);
          render();
        });
      }
      setQty(card, qtyOf(card));
    });

    if (addBtn) {
      addBtn.addEventListener('click', function () {
        var picked = cards.filter(function (card) { return qtyOf(card) > 0; });
        if (!picked.length || addBtn.dataset.busy) return;
        addBtn.dataset.busy = '1';
        var cart = document.querySelector('vitgem-cart-drawer') || document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        var body = {
          items: picked.map(function (card) {
            return { id: +card.dataset.vgVariant, quantity: qtyOf(card) };
          })
        };
        if (cart && typeof cart.getSectionsToRender === 'function') {
          body.sections = cart.getSectionsToRender().map(function (s) { return s.id; });
          body.sections_url = window.location.pathname;
        }
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(body)
        })
          .then(function (r) { return r.json(); })
          .then(function (res) {
            delete addBtn.dataset.busy;
            if (res.status) return; // add error (e.g. out of stock) — leave page as-is
            if (cart && typeof cart.renderContents === 'function') cart.renderContents(res);
            if (cart && typeof cart.open === 'function') cart.open(addBtn);
            else window.location.href = '/cart';
            try {
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
                publish(PUB_SUB_EVENTS.cartUpdate, { source: 'vitgem-os2-builder' });
              }
            } catch (e) {}
            cards.forEach(function (card) { setQty(card, 0); });
            render();
          })
          .catch(function () {
            delete addBtn.dataset.busy;
            window.location.href = '/cart';
          });
      });
    }
    render();
  }

  /* ---- boot ------------------------------------------------------------- */
  function init() {
    observeReveals();
    observeBars();
    wireHeader();
    wireMenu();
    wireStickyBuy();
    wireReviewRail();
    wireFilm();
    wireSubToggle();
    wireFaq();
    wirePdp();
    wireGallery();
    wireCollection();
    wireBuilder();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // theme editor re-renders sections in place — re-wire whatever it touched
  document.addEventListener('shopify:section:load', init);
})();
